'use client';

import React, {
  type ComponentPropsWithoutRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useGalleryImageProviderContext } from '@/components/providers/GalleryImageProvider';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  type CardProps,
} from '@/components/ui/card';
import { Gallery } from '@/components/ui/Gallery';
import { MotionLink } from '@/components/ui/Motion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  TypographyH2,
  TypographyH3,
  TypographySmall,
} from '@/components/ui/typography';
import { GALLERY_TAG, URLS } from '@/lib/consts';
import { capitalizeFirstCharacter, cls, cn } from '@/lib/utils';
import type { GalleryFilter } from '../../providers/GalleryImageProvider/gallery-image-provider';
import Flex from '../layout/flex';
import Wrapper from '../layout/wrapper';

export interface GalleryCardProperties extends ComponentPropsWithoutRef<'div'> {
  title?: string;
  description?: string;
  sub?: string;
  url?: string;
  showCTA?: boolean;
  showFilter?: boolean;
  variant?: CardProps['variant'];
}

function GalleryCard({
  title,
  description,
  sub,
  className = '',
  url = URLS.PROJECTS,
  showCTA = true,
  showFilter = true,
  variant = 'default',
}: GalleryCardProperties) {
  const { error, filter, setFilter, filterTags, isLoadingFilterTags } =
    useGalleryImageProviderContext();

  const showHeader = useMemo(() => {
    if (title) return true;

    if (description) return true;

    if (sub) return true;

    return false;
  }, [title, description, sub]);

  const handleSelectOnChange = useCallback(
    (value: GalleryFilter) => {
      setFilter(value);
    },
    [setFilter],
  );

  useEffect(() => {
    if (!error) {
      return;
    }

    toast.error(
      `Oops! Looks like the photos for the Gallery are failing to fetch. Please check your internet or try again!`,
      { dismissible: true },
    );
  }, [error]);

  if (error) {
    return null;
  }

  return (
    <Card className={cn(cls`w-full`, className)} variant={variant}>
      {showHeader && (
        <CardHeader className={cls`p-2 md:p-5 text-center`}>
          {sub && (
            <TypographyH3 className="font-bold text-primary">{sub}</TypographyH3>
          )}

          {title && (
            <TypographyH2 className={cls`border-b-0`}>{title}</TypographyH2>
          )}

          {description && <TypographySmall>{description}</TypographySmall>}
        </CardHeader>
      )}

      <CardContent
        className={cls`
          flex flex-col justify-center items-center
          gap-2 md:gap-5
          p-2 md:p-5
          pt-0
        `}
      >
        <Wrapper padding="p-4 md:p-6" asChild>
          <Flex
            direction="column"
            items="center"
            gap="gap-4"
            className="w-full"
          >
            {/* Gallery */}
            <Gallery />

            {/* Filter Dropdown */}
            {showFilter && !isLoadingFilterTags && filterTags.length > 0 && (
              <Select
                onValueChange={handleSelectOnChange}
                defaultValue={filter}
              >
                <SelectTrigger className={cls`w-fit`}>
                  <SelectValue placeholder="Select a filter" />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value={GALLERY_TAG}>Default</SelectItem>
                  {filterTags.map(f => (
                    <SelectItem key={f} value={f}>
                      {capitalizeFirstCharacter(f)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* CTA Button */}
            {showCTA && (
              <>
                <Button asChild className={cls`w-fit`}>
                  <MotionLink href={url}>
                    <Eye className={cls`mr-2`} />
                    View More
                  </MotionLink>
                </Button>
              </>
            )}
          </Flex>
        </Wrapper>
      </CardContent>
    </Card>
  );
}

export default memo(GalleryCard);
